interface EmergencyContact {
  service: string;
  number: string;
  available: string;
}

interface MedicalFacility {
  name: string;
  type: 'hospital' | 'clinic' | 'pharmacy';
  address: string;
  phone?: string;
  hours?: string;
}

interface EmergencyPhrase {
  english: string;
  local: string;
  pronunciation?: string;
}

interface EmergencyResources {
  emergencyNumbers: EmergencyContact[];
  medicalFacilities: MedicalFacility[];
  travelHotlines: EmergencyContact[];
  keyPhrases: EmergencyPhrase[];
}

// Emergency resources by country/region
const emergencyResourcesDatabase: { [key: string]: EmergencyResources } = {
  // Default/International
  default: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'Police', number: 'Contact local authorities', available: '24/7' },
      { service: 'Ambulance', number: 'Contact local authorities', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Nearest Hospital', type: 'hospital', address: 'Check Google Maps for nearest hospital' },
      { name: 'Nearest Pharmacy', type: 'pharmacy', address: 'Check Google Maps for nearest pharmacy' },
    ],
    travelHotlines: [
      { service: 'International SOS', number: '+1-215-942-8226', available: '24/7' },
      { service: 'US Embassy Global', number: '+1-202-501-4444', available: '24/7' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'Check local translation' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Pharmacy', local: 'Pharmacy' },
      { english: 'Help me', local: 'Help me' },
    ],
  },

  // Thailand
  thailand: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '191', available: '24/7' },
      { service: 'Tourist Police', number: '1155', available: '24/7' },
      { service: 'Ambulance', number: '1669', available: '24/7' },
      { service: 'Fire', number: '199', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Bumrungrad International Hospital', type: 'hospital', address: '33 Sukhumvit 3, Bangkok', phone: '+66 2-066-8888', hours: '24/7' },
      { name: 'Bangkok Hospital', type: 'hospital', address: '2 Soi Soonvijai 7, Bangkok', phone: '+66 2-310-3000', hours: '24/7' },
      { name: 'Samitivej Hospital', type: 'hospital', address: '133 Sukhumvit 49, Bangkok', phone: '+66 2-711-8000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'Tourist Assistance', number: '1672', available: '24/7' },
      { service: 'US Embassy Bangkok', number: '+66 2-205-4000', available: 'Mon-Fri 7:00-16:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'ฉันต้องการหมอ', pronunciation: 'chan dtong gaan mor' },
      { english: 'Hospital', local: 'โรงพยาบาล', pronunciation: 'rohng pá-yaa-baan' },
      { english: 'Pharmacy', local: 'ร้านขายยา', pronunciation: 'ráan kăai yaa' },
      { english: 'Help me', local: 'ช่วยด้วย', pronunciation: 'chûay dûay' },
      { english: 'Call ambulance', local: 'เรียกรถพยาบาล', pronunciation: 'rîiak rót pá-yaa-baan' },
    ],
  },

  // Japan
  japan: {
    emergencyNumbers: [
      { service: 'Police', number: '110', available: '24/7' },
      { service: 'Ambulance/Fire', number: '119', available: '24/7' },
      { service: 'Japan Helpline (English)', number: '0570-000-911', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Tokyo Medical and Surgical Clinic', type: 'clinic', address: '32 Shiba-koen, Minato-ku, Tokyo', phone: '+81-3-3436-3028', hours: 'Mon-Fri 9:00-18:00, Sat 9:00-13:00' },
      { name: 'St. Luke\'s International Hospital', type: 'hospital', address: '9-1 Akashi-cho, Chuo-ku, Tokyo', phone: '+81-3-3541-5151', hours: '24/7' },
      { name: 'International Clinic', type: 'clinic', address: '1-5-9 Azabudai, Minato-ku, Tokyo', phone: '+81-3-3582-2646', hours: 'Mon-Fri 9:00-17:00' },
    ],
    travelHotlines: [
      { service: 'Japan Visitor Hotline', number: '050-3816-2787', available: '24/7 (English, Chinese, Korean)' },
      { service: 'US Embassy Tokyo', number: '+81-3-3224-5000', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: '医者が必要です', pronunciation: 'isha ga hitsuyō desu' },
      { english: 'Hospital', local: '病院', pronunciation: 'byōin' },
      { english: 'Pharmacy', local: '薬局', pronunciation: 'yakkyoku' },
      { english: 'Help me', local: '助けて', pronunciation: 'tasukete' },
      { english: 'Call ambulance', local: '救急車を呼んで', pronunciation: 'kyūkyūsha wo yonde' },
    ],
  },

  // France
  france: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'SAMU (Medical)', number: '15', available: '24/7' },
      { service: 'Police', number: '17', available: '24/7' },
      { service: 'Fire Brigade', number: '18', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'American Hospital of Paris', type: 'hospital', address: '63 Boulevard Victor Hugo, 92200 Neuilly-sur-Seine', phone: '+33 1-46-41-25-25', hours: '24/7' },
      { name: 'Hôpital Cochin', type: 'hospital', address: '27 Rue du Faubourg Saint-Jacques, 75014 Paris', phone: '+33 1-58-41-41-41', hours: '24/7' },
      { name: 'Centre Médical Europe', type: 'clinic', address: '44 Rue d\'Amsterdam, 75009 Paris', phone: '+33 1-42-81-93-33', hours: 'Mon-Fri 8:00-20:00, Sat 8:00-18:00' },
    ],
    travelHotlines: [
      { service: 'SOS Médecins (House Calls)', number: '3624', available: '24/7' },
      { service: 'US Embassy Paris', number: '+33 1-43-12-22-22', available: 'Mon-Fri 9:00-18:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'J\'ai besoin d\'un médecin', pronunciation: 'zhay bez-wan dun med-san' },
      { english: 'Hospital', local: 'Hôpital', pronunciation: 'oh-pee-tal' },
      { english: 'Pharmacy', local: 'Pharmacie', pronunciation: 'far-mah-see' },
      { english: 'Help me', local: 'Aidez-moi', pronunciation: 'ay-day mwah' },
      { english: 'Call ambulance', local: 'Appelez une ambulance', pronunciation: 'ap-lay oon ahm-boo-lahns' },
    ],
  },

  // Spain
  spain: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'Police', number: '091', available: '24/7' },
      { service: 'Ambulance', number: '061', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Hospital Universitario La Paz', type: 'hospital', address: 'Paseo de la Castellana 261, Madrid', phone: '+34 91-727-70-00', hours: '24/7' },
      { name: 'Hospital Clínic Barcelona', type: 'hospital', address: 'Carrer de Villarroel 170, Barcelona', phone: '+34 93-227-54-00', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Madrid', number: '+34 91-587-2200', available: 'Mon-Fri 9:00-18:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Necesito un médico', pronunciation: 'neh-seh-see-toh oon meh-dee-koh' },
      { english: 'Hospital', local: 'Hospital', pronunciation: 'os-pee-tal' },
      { english: 'Pharmacy', local: 'Farmacia', pronunciation: 'far-mah-thee-ah' },
      { english: 'Help me', local: 'Ayúdame', pronunciation: 'ah-yoo-dah-meh' },
      { english: 'Call ambulance', local: 'Llame una ambulancia', pronunciation: 'yah-meh oo-nah ahm-boo-lahn-thee-ah' },
    ],
  },

  // United Kingdom
  'united kingdom': {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '999 or 112', available: '24/7' },
      { service: 'NHS Non-Emergency', number: '111', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'St Thomas\' Hospital', type: 'hospital', address: 'Westminster Bridge Road, London SE1 7EH', phone: '+44 20-7188-7188', hours: '24/7' },
      { name: 'The London Clinic', type: 'clinic', address: '20 Devonshire Place, London W1G 6BW', phone: '+44 20-7935-4444', hours: 'Mon-Fri 8:00-20:00' },
    ],
    travelHotlines: [
      { service: 'US Embassy London', number: '+44 20-7499-9000', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English speaking' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Pharmacy', local: 'Chemist' },
      { english: 'Help me', local: 'Help me' },
      { english: 'Call ambulance', local: 'Call an ambulance' },
    ],
  },

  // Mexico
  mexico: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '911', available: '24/7' },
      { service: 'Red Cross Ambulance', number: '065', available: '24/7' },
      { service: 'Tourist Police', number: '078', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'ABC Medical Center', type: 'hospital', address: 'Av. Carlos Graef Fernández 154, Mexico City', phone: '+52 55-5230-8000', hours: '24/7' },
      { name: 'Hospital Ángeles', type: 'hospital', address: 'Av. Vasco de Quiroga 4001, Mexico City', phone: '+52 55-5246-5000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Mexico City', number: '+52 55-5080-2000', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Necesito un médico', pronunciation: 'neh-seh-see-toh oon meh-dee-koh' },
      { english: 'Hospital', local: 'Hospital', pronunciation: 'os-pee-tal' },
      { english: 'Pharmacy', local: 'Farmacia', pronunciation: 'far-mah-see-ah' },
      { english: 'Help me', local: 'Ayúdame', pronunciation: 'ah-yoo-dah-meh' },
      { english: 'Call ambulance', local: 'Llame una ambulancia', pronunciation: 'yah-meh oo-nah ahm-boo-lahn-see-ah' },
    ],
  },

  // Italy
  italy: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'Medical Emergency', number: '118', available: '24/7' },
      { service: 'Police', number: '113', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Rome American Hospital', type: 'hospital', address: 'Via Emilio Longoni 69, Rome', phone: '+39 06-225-51', hours: '24/7' },
      { name: 'Policlinico Gemelli', type: 'hospital', address: 'Largo Agostino Gemelli 8, Rome', phone: '+39 06-30-151', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Rome', number: '+39 06-4674-1', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Ho bisogno di un medico', pronunciation: 'oh bee-zohn-yoh dee oon meh-dee-koh' },
      { english: 'Hospital', local: 'Ospedale', pronunciation: 'os-peh-dah-leh' },
      { english: 'Pharmacy', local: 'Farmacia', pronunciation: 'far-mah-chee-ah' },
      { english: 'Help me', local: 'Aiutami', pronunciation: 'eye-oo-tah-mee' },
      { english: 'Call ambulance', local: 'Chiama un\'ambulanza', pronunciation: 'kee-ah-mah oon ahm-boo-lahn-tsah' },
    ],
  },

  // United States
  'united states': {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '911', available: '24/7' },
      { service: 'Poison Control', number: '1-800-222-1222', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Check Google Maps', type: 'hospital', address: 'Search for nearest hospital or urgent care' },
      { name: 'CVS MinuteClinic', type: 'clinic', address: 'Multiple locations nationwide', hours: 'Varies by location' },
    ],
    travelHotlines: [
      { service: 'Medical Referral Service', number: '1-800-DOCTORS', available: '24/7' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English speaking' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Pharmacy', local: 'Pharmacy' },
      { english: 'Help me', local: 'Help me' },
      { english: 'Call ambulance', local: 'Call an ambulance' },
    ],
  },

  // Canada
  canada: {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '911', available: '24/7' },
      { service: 'Poison Control', number: 'Varies by province', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Toronto General Hospital', type: 'hospital', address: '200 Elizabeth St, Toronto', phone: '+1 416-340-4800', hours: '24/7' },
      { name: 'Vancouver General Hospital', type: 'hospital', address: '899 W 12th Ave, Vancouver', phone: '+1 604-875-4111', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Ottawa', number: '+1 613-688-5335', available: 'Mon-Fri 8:30-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor / J\'ai besoin d\'un médecin', pronunciation: 'English/French speaking' },
      { english: 'Hospital', local: 'Hospital / Hôpital' },
      { english: 'Pharmacy', local: 'Pharmacy / Pharmacie' },
      { english: 'Help me', local: 'Help me / Aidez-moi' },
      { english: 'Call ambulance', local: 'Call an ambulance / Appelez une ambulance' },
    ],
  },

  // Australia
  australia: {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '000', available: '24/7' },
      { service: 'Poison Information', number: '13 11 26', available: '24/7' },
      { service: 'Health Direct', number: '1800 022 222', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Royal Prince Alfred Hospital', type: 'hospital', address: 'Missenden Rd, Camperdown, Sydney', phone: '+61 2-9515-6111', hours: '24/7' },
      { name: 'The Alfred Hospital', type: 'hospital', address: 'Commercial Rd, Melbourne', phone: '+61 3-9076-2000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Canberra', number: '+61 2-6214-5600', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English speaking' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Pharmacy', local: 'Chemist' },
      { english: 'Help me', local: 'Help me' },
      { english: 'Call ambulance', local: 'Call an ambulance' },
    ],
  },

  // Germany
  germany: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'Police', number: '110', available: '24/7' },
      { service: 'Medical On-Call Service', number: '116117', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Charité University Hospital', type: 'hospital', address: 'Charitéplatz 1, Berlin', phone: '+49 30-450-50', hours: '24/7' },
      { name: 'Munich University Hospital', type: 'hospital', address: 'Marchioninistraße 15, Munich', phone: '+49 89-4400-0', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Berlin', number: '+49 30-8305-0', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Ich brauche einen Arzt', pronunciation: 'ish brow-kheh eye-nen artst' },
      { english: 'Hospital', local: 'Krankenhaus', pronunciation: 'krahn-ken-house' },
      { english: 'Pharmacy', local: 'Apotheke', pronunciation: 'ah-poh-tay-keh' },
      { english: 'Help me', local: 'Hilfe', pronunciation: 'heel-feh' },
      { english: 'Call ambulance', local: 'Rufen Sie einen Krankenwagen', pronunciation: 'roo-fen zee eye-nen krahn-ken-vah-gen' },
    ],
  },

  // Greece
  greece: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'Ambulance', number: '166', available: '24/7' },
      { service: 'Tourist Police', number: '171', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Athens Medical Center', type: 'hospital', address: '5-7 Distomou St, Athens', phone: '+30 210-686-9000', hours: '24/7' },
      { name: 'KAT Hospital', type: 'hospital', address: '2 Nikis St, Athens', phone: '+30 213-208-6000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Athens', number: '+30 210-721-2951', available: 'Mon-Fri 8:30-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Χρειάζομαι γιατρό', pronunciation: 'khree-ah-zoh-meh yah-troh' },
      { english: 'Hospital', local: 'Νοσοκομείο', pronunciation: 'noh-soh-koh-mee-oh' },
      { english: 'Pharmacy', local: 'Φαρμακείο', pronunciation: 'far-mah-kee-oh' },
      { english: 'Help me', local: 'Βοηθήστε με', pronunciation: 'voh-ee-thee-steh meh' },
      { english: 'Call ambulance', local: 'Καλέστε ασθενοφόρο', pronunciation: 'kah-leh-steh ah-sthen-oh-foh-roh' },
    ],
  },

  // Portugal
  portugal: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'SNS Health Line', number: '808 24 24 24', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Hospital da Luz', type: 'hospital', address: 'Av. Lusíada 100, Lisbon', phone: '+351 21-770-4000', hours: '24/7' },
      { name: 'Hospital de São João', type: 'hospital', address: 'Alameda Prof. Hernâni Monteiro, Porto', phone: '+351 22-551-2100', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Lisbon', number: '+351 21-727-3300', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Preciso de um médico', pronunciation: 'preh-see-zoh deh oom meh-dee-koh' },
      { english: 'Hospital', local: 'Hospital', pronunciation: 'osh-pee-tal' },
      { english: 'Pharmacy', local: 'Farmácia', pronunciation: 'far-mah-see-ah' },
      { english: 'Help me', local: 'Ajude-me', pronunciation: 'ah-zhoo-deh-meh' },
      { english: 'Call ambulance', local: 'Chame uma ambulância', pronunciation: 'shah-meh oo-mah ahm-boo-lahn-see-ah' },
    ],
  },

  // UAE (Dubai/Abu Dhabi)
  uae: {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '999', available: '24/7' },
      { service: 'Ambulance', number: '998', available: '24/7' },
      { service: 'Police', number: '999', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'American Hospital Dubai', type: 'hospital', address: 'Oud Metha, Dubai', phone: '+971 4-336-7777', hours: '24/7' },
      { name: 'Cleveland Clinic Abu Dhabi', type: 'hospital', address: 'Al Maryah Island, Abu Dhabi', phone: '+971 2-501-9999', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Abu Dhabi', number: '+971 2-414-2200', available: 'Sun-Thu 8:00-16:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'أحتاج إلى طبيب', pronunciation: 'ah-taj ee-la ta-beeb' },
      { english: 'Hospital', local: 'مستشفى', pronunciation: 'mus-tash-fa' },
      { english: 'Pharmacy', local: 'صيدلية', pronunciation: 'say-da-lee-ya' },
      { english: 'Help me', local: 'ساعدني', pronunciation: 'sah-id-nee' },
      { english: 'Call ambulance', local: 'اتصل بالإسعاف', pronunciation: 'it-ta-sil bil-is-aaf' },
    ],
  },

  // Singapore
  singapore: {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '995', available: '24/7' },
      { service: 'Police', number: '999', available: '24/7' },
      { service: 'Non-Emergency Ambulance', number: '1777', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Singapore General Hospital', type: 'hospital', address: 'Outram Road, Singapore', phone: '+65 6222-3322', hours: '24/7' },
      { name: 'Mount Elizabeth Hospital', type: 'hospital', address: '3 Mount Elizabeth, Singapore', phone: '+65 6731-2218', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Singapore', number: '+65 6476-9100', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English widely spoken' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Pharmacy', local: 'Pharmacy' },
      { english: 'Help me', local: 'Help me' },
      { english: 'Call ambulance', local: 'Call an ambulance' },
    ],
  },

  // South Korea
  'south korea': {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '119', available: '24/7' },
      { service: 'Police', number: '112', available: '24/7' },
      { service: 'Medical Emergency (English)', number: '1339', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Samsung Medical Center', type: 'hospital', address: '81 Irwon-ro, Gangnam-gu, Seoul', phone: '+82 2-3410-2114', hours: '24/7' },
      { name: 'Seoul National University Hospital', type: 'hospital', address: '101 Daehak-ro, Jongno-gu, Seoul', phone: '+82 2-2072-2114', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Seoul', number: '+82 2-397-4114', available: 'Mon-Fri 8:30-17:30' },
      { service: 'Korea Medical Tourism', number: '1577-7129', available: '24/7' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: '의사가 필요해요', pronunciation: 'ui-sa-ga pil-yo-hae-yo' },
      { english: 'Hospital', local: '병원', pronunciation: 'byeong-won' },
      { english: 'Pharmacy', local: '약국', pronunciation: 'yak-guk' },
      { english: 'Help me', local: '도와주세요', pronunciation: 'do-wa-ju-se-yo' },
      { english: 'Call ambulance', local: '구급차를 불러주세요', pronunciation: 'gu-geup-cha-reul bul-leo-ju-se-yo' },
    ],
  },

  // Vietnam
  vietnam: {
    emergencyNumbers: [
      { service: 'Ambulance', number: '115', available: '24/7' },
      { service: 'Police', number: '113', available: '24/7' },
      { service: 'Fire', number: '114', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Family Medical Practice', type: 'clinic', address: 'Diamond Plaza, Ho Chi Minh City', phone: '+84 28-3822-7848', hours: '24/7' },
      { name: 'Vinmec International Hospital', type: 'hospital', address: '458 Minh Khai, Hanoi', phone: '+84 24-3974-3556', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Hanoi', number: '+84 24-3850-5000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Tôi cần bác sĩ', pronunciation: 'toy kan bahk see' },
      { english: 'Hospital', local: 'Bệnh viện', pronunciation: 'behn vee-en' },
      { english: 'Pharmacy', local: 'Nhà thuốc', pronunciation: 'nya took' },
      { english: 'Help me', local: 'Giúp tôi', pronunciation: 'yoop toy' },
      { english: 'Call ambulance', local: 'Gọi xe cấp cứu', pronunciation: 'goy seh kahp koo' },
    ],
  },

  // Indonesia
  indonesia: {
    emergencyNumbers: [
      { service: 'Ambulance', number: '118', available: '24/7' },
      { service: 'Police', number: '110', available: '24/7' },
      { service: 'General Emergency', number: '112', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'BIMC Hospital', type: 'hospital', address: 'Jl. Bypass Ngurah Rai, Bali', phone: '+62 361-761-263', hours: '24/7' },
      { name: 'RS Premier Bintaro', type: 'hospital', address: 'Jl. MH Thamrin, Jakarta', phone: '+62 21-745-5500', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Jakarta', number: '+62 21-5083-1000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Saya perlu dokter', pronunciation: 'sah-yah per-loo dok-ter' },
      { english: 'Hospital', local: 'Rumah sakit', pronunciation: 'roo-mah sah-kit' },
      { english: 'Pharmacy', local: 'Apotek', pronunciation: 'ah-poh-tek' },
      { english: 'Help me', local: 'Tolong saya', pronunciation: 'toh-long sah-yah' },
      { english: 'Call ambulance', local: 'Panggil ambulans', pronunciation: 'pahng-gil ahm-boo-lahns' },
    ],
  },

  // New Zealand
  'new zealand': {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '111', available: '24/7' },
      { service: 'Healthline', number: '0800 611 116', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Auckland City Hospital', type: 'hospital', address: '2 Park Rd, Grafton, Auckland', phone: '+64 9-367-0000', hours: '24/7' },
      { name: 'Wellington Hospital', type: 'hospital', address: 'Riddiford St, Newtown, Wellington', phone: '+64 4-385-5999', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Wellington', number: '+64 4-462-6000', available: 'Mon-Fri 9:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English speaking' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Pharmacy', local: 'Pharmacy' },
      { english: 'Help me', local: 'Help me' },
      { english: 'Call ambulance', local: 'Call an ambulance' },
    ],
  },

  // Netherlands
  netherlands: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'Non-Emergency Medical', number: '0900-8844', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Amsterdam UMC', type: 'hospital', address: 'Meibergdreef 9, Amsterdam', phone: '+31 20-566-9111', hours: '24/7' },
      { name: 'Erasmus MC', type: 'hospital', address: 'Dr. Molewaterplein 40, Rotterdam', phone: '+31 10-704-0704', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy The Hague', number: '+31 70-310-2209', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Ik heb een dokter nodig', pronunciation: 'ik heb en dok-ter noh-dikh' },
      { english: 'Hospital', local: 'Ziekenhuis', pronunciation: 'zee-ken-hois' },
      { english: 'Pharmacy', local: 'Apotheek', pronunciation: 'ah-poh-teyk' },
      { english: 'Help me', local: 'Help me', pronunciation: 'help meh' },
      { english: 'Call ambulance', local: 'Bel een ambulance', pronunciation: 'bel en ahm-bew-lahns' },
    ],
  },

  // Switzerland
  switzerland: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'Ambulance', number: '144', available: '24/7' },
      { service: 'Police', number: '117', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'University Hospital Zurich', type: 'hospital', address: 'Rämistrasse 100, Zurich', phone: '+41 44-255-1111', hours: '24/7' },
      { name: 'Geneva University Hospitals', type: 'hospital', address: 'Rue Gabrielle-Perret-Gentil 4, Geneva', phone: '+41 22-372-3311', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Bern', number: '+41 31-357-7011', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Ich brauche einen Arzt / J\'ai besoin d\'un médecin', pronunciation: 'German/French/Italian spoken' },
      { english: 'Hospital', local: 'Krankenhaus / Hôpital' },
      { english: 'Pharmacy', local: 'Apotheke / Pharmacie' },
      { english: 'Help me', local: 'Hilfe / Aidez-moi' },
      { english: 'Call ambulance', local: 'Rufen Sie einen Krankenwagen / Appelez une ambulance' },
    ],
  },

  // Turkey
  turkey: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
      { service: 'Police', number: '155', available: '24/7' },
      { service: 'Tourist Police', number: '153', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'American Hospital Istanbul', type: 'hospital', address: 'Güzelbahçe Sk. No:20, Istanbul', phone: '+90 212-444-3777', hours: '24/7' },
      { name: 'Acıbadem Hospital', type: 'hospital', address: 'Multiple locations', phone: '+90 444-2422', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Ankara', number: '+90 312-455-5555', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Doktora ihtiyacım var', pronunciation: 'dok-toh-rah ee-tee-yah-jum vahr' },
      { english: 'Hospital', local: 'Hastane', pronunciation: 'has-tah-neh' },
      { english: 'Pharmacy', local: 'Eczane', pronunciation: 'ej-zah-neh' },
      { english: 'Help me', local: 'Yardım edin', pronunciation: 'yar-dum eh-din' },
      { english: 'Call ambulance', local: 'Ambulans çağırın', pronunciation: 'ahm-boo-lahns chah-uh-run' },
    ],
  },

  // Brazil
  brazil: {
    emergencyNumbers: [
      { service: 'Ambulance', number: '192', available: '24/7' },
      { service: 'Police', number: '190', available: '24/7' },
      { service: 'General Emergency', number: '193', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Hospital Sírio-Libanês', type: 'hospital', address: 'R. Dona Adma Jafet, São Paulo', phone: '+55 11-3394-5000', hours: '24/7' },
      { name: 'Hospital Copa Star', type: 'hospital', address: 'R. Figueiredo de Magalhães, Rio de Janeiro', phone: '+55 21-2545-3600', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Brasília', number: '+55 61-3312-7000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Preciso de um médico', pronunciation: 'preh-see-zoh jee oom meh-jee-koh' },
      { english: 'Hospital', local: 'Hospital', pronunciation: 'os-pee-tahl' },
      { english: 'Pharmacy', local: 'Farmácia', pronunciation: 'far-mah-see-ah' },
      { english: 'Help me', local: 'Me ajude', pronunciation: 'mee ah-zhoo-jee' },
      { english: 'Call ambulance', local: 'Chame uma ambulância', pronunciation: 'shah-mee oo-mah ahm-boo-lahn-see-ah' },
    ],
  },

  // Iceland
  iceland: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '112', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Landspítali University Hospital', type: 'hospital', address: 'Hringbraut, Reykjavík', phone: '+354 543-1000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Reykjavik', number: '+354 595-2200', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Ég þarf lækni', pronunciation: 'yeh tharf laik-nih' },
      { english: 'Hospital', local: 'Sjúkrahús', pronunciation: 'syook-rah-hoos' },
      { english: 'Pharmacy', local: 'Apótek', pronunciation: 'ah-poh-tek' },
      { english: 'Help me', local: 'Hjálpaðu mér', pronunciation: 'hyahl-pah-thu myehr' },
      { english: 'Call ambulance', local: 'Hringdu í sjúkrabíl', pronunciation: 'hring-doo ee syook-rah-beel' },
    ],
  },

  // India
  india: {
    emergencyNumbers: [
      { service: 'Ambulance', number: '102', available: '24/7' },
      { service: 'Police', number: '100', available: '24/7' },
      { service: 'General Emergency', number: '112', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Apollo Hospital', type: 'hospital', address: 'Multiple locations nationwide', phone: '+91 1860-500-1066', hours: '24/7' },
      { name: 'Fortis Hospital', type: 'hospital', address: 'Multiple locations', phone: '+91 1800-102-2222', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy New Delhi', number: '+91 11-2419-8000', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'मुझे डॉक्टर चाहिए', pronunciation: 'mu-jhe doc-ter cha-hee-eh' },
      { english: 'Hospital', local: 'अस्पताल', pronunciation: 'as-pa-taal' },
      { english: 'Pharmacy', local: 'दवाखाना', pronunciation: 'da-wa-kha-na' },
      { english: 'Help me', local: 'मेरी मदद करो', pronunciation: 'meh-ree ma-dad ka-ro' },
      { english: 'Call ambulance', local: 'एम्बुलेंस बुलाओ', pronunciation: 'am-bu-lens bu-la-o' },
    ],
  },

  // China
  china: {
    emergencyNumbers: [
      { service: 'Ambulance', number: '120', available: '24/7' },
      { service: 'Police', number: '110', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Beijing United Family Hospital', type: 'hospital', address: '2 Jiangtai Rd, Chaoyang, Beijing', phone: '+86 10-5927-7000', hours: '24/7' },
      { name: 'Shanghai United Family Hospital', type: 'hospital', address: '1139 Xianxia Rd, Shanghai', phone: '+86 21-2216-3999', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Beijing', number: '+86 10-8531-3000', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: '我需要医生', pronunciation: 'wo xu-yao yi-sheng' },
      { english: 'Hospital', local: '医院', pronunciation: 'yi-yuan' },
      { english: 'Pharmacy', local: '药店', pronunciation: 'yao-dian' },
      { english: 'Help me', local: '救命', pronunciation: 'jiu-ming' },
      { english: 'Call ambulance', local: '叫救护车', pronunciation: 'jiao jiu-hu-che' },
    ],
  },

  // Malaysia
  malaysia: {
    emergencyNumbers: [
      { service: 'General Emergency', number: '999', available: '24/7' },
      { service: 'Ambulance', number: '999', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Gleneagles Kuala Lumpur', type: 'hospital', address: '286, Jalan Ampang, Kuala Lumpur', phone: '+60 3-4141-3000', hours: '24/7' },
      { name: 'Prince Court Medical Centre', type: 'hospital', address: '39, Jalan Kia Peng, Kuala Lumpur', phone: '+60 3-2160-0000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Kuala Lumpur', number: '+60 3-2168-5000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Saya perlukan doktor', pronunciation: 'sah-yah per-loo-kahn dok-tor' },
      { english: 'Hospital', local: 'Hospital', pronunciation: 'hos-pee-tal' },
      { english: 'Pharmacy', local: 'Farmasi', pronunciation: 'far-mah-see' },
      { english: 'Help me', local: 'Tolong saya', pronunciation: 'toh-long sah-yah' },
      { english: 'Call ambulance', local: 'Panggil ambulans', pronunciation: 'pahng-gil ahm-boo-lahns' },
    ],
  },

  // Cyprus
  cyprus: {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '112', available: '24/7' },
      { service: 'Police', number: '112', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Nicosia General Hospital', type: 'hospital', address: 'Nicosia', phone: '+357 22-603000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Nicosia', number: '+357 22-393939', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Χρειάζομαι γιατρό', pronunciation: 'hree-ah-zoh-meh yah-troh' },
      { english: 'Hospital', local: 'Νοσοκομείο', pronunciation: 'noh-soh-koh-mee-oh' },
      { english: 'Help me', local: 'Βοηθήστε με', pronunciation: 'voh-ee-thee-steh meh' },
    ],
  },

  // Malta
  malta: {
    emergencyNumbers: [
      { service: 'Emergency Services', number: '112', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Mater Dei Hospital', type: 'hospital', address: 'Msida', phone: '+356 2545-0000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Valletta', number: '+356 2561-4000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English speaking' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Help me', local: 'Help me' },
    ],
  },

  // Andorra
  andorra: {
    emergencyNumbers: [
      { service: 'Police', number: '110', available: '24/7' },
      { service: 'Medical', number: '116', available: '24/7' },
      { service: 'Fire', number: '118', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Hospital Nostra Senyora de Meritxell', type: 'hospital', address: 'Andorra la Vella', phone: '+376 871000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Paris (covers Andorra)', number: '+33 1-43-12-22-22', available: 'Mon-Fri' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Necessito un metge', pronunciation: 'neh-seh-see-toh oon meht-geh' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Help me', local: 'Ajuda\'m', pronunciation: 'ah-zhoo-dahm' },
    ],
  },

  // Luxembourg
  luxembourg: {
    emergencyNumbers: [
      { service: 'Police', number: '113', available: '24/7' },
      { service: 'Medical', number: '112', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Centre Hospitalier de Luxembourg', type: 'hospital', address: 'Luxembourg City', phone: '+352 4411-1', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Luxembourg', number: '+352 46-01-23', available: 'Mon-Fri 8:30-17:30' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Ich brauche einen Arzt', pronunciation: 'eekh brow-kheh eye-nen artst' },
      { english: 'Hospital', local: 'Krankenhaus' },
      { english: 'Help me', local: 'Helfen Sie mir', pronunciation: 'hel-fen zee meer' },
    ],
  },

  // Gibraltar
  gibraltar: {
    emergencyNumbers: [
      { service: 'Police', number: '199', available: '24/7' },
      { service: 'Medical/Fire', number: '190', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'St Bernard\'s Hospital', type: 'hospital', address: 'Gibraltar', phone: '+350 20007-2355', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Consulate General Gibraltar', number: '+350 200-78622', available: 'Mon-Fri' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English speaking' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Help me', local: 'Help me' },
    ],
  },

  // Kosovo
  kosovo: {
    emergencyNumbers: [
      { service: 'Police', number: '192', available: '24/7' },
      { service: 'Medical', number: '194', available: '24/7' },
      { service: 'Fire', number: '193', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'University Clinical Centre of Kosovo', type: 'hospital', address: 'Pristina', phone: '+383 38-500-500', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Pristina', number: '+383 38-59-59-3000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Më duhet një doktor', pronunciation: 'muh doo-het nyuh dok-tor' },
      { english: 'Hospital', local: 'Spital' },
      { english: 'Help me', local: 'Më ndihmoni', pronunciation: 'muh ndee-hmoh-nee' },
    ],
  },

  // North Macedonia
  'north macedonia': {
    emergencyNumbers: [
      { service: 'Police', number: '192', available: '24/7' },
      { service: 'Medical', number: '194', available: '24/7' },
      { service: 'Fire', number: '193', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'City Hospital 8th September', type: 'hospital', address: 'Skopje', phone: '+389 2-3147-147', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Skopje', number: '+389 2-310-2000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Ми треба доктор', pronunciation: 'mee treh-bah dok-tor' },
      { english: 'Hospital', local: 'Болница', pronunciation: 'bol-nee-tsa' },
      { english: 'Help me', local: 'Помогнете ми', pronunciation: 'poh-mog-neh-teh mee' },
    ],
  },

  // Ukraine
  ukraine: {
    emergencyNumbers: [
      { service: 'Police', number: '102', available: '24/7' },
      { service: 'Medical', number: '103', available: '24/7' },
      { service: 'Fire', number: '101', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Kyiv City Clinical Hospital', type: 'hospital', address: 'Kyiv', phone: '+380 44-486-8024', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'Check current US Embassy status', number: '+380 44-521-5000', available: 'Verify availability' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Мені потрібен лікар', pronunciation: 'meh-nee poh-tree-ben lee-kar' },
      { english: 'Hospital', local: 'Лікарня', pronunciation: 'lee-kar-nya' },
      { english: 'Help me', local: 'Допоможіть мені', pronunciation: 'doh-poh-moh-zheet meh-nee' },
    ],
  },

  // Russia
  russia: {
    emergencyNumbers: [
      { service: 'Police', number: '102', available: '24/7' },
      { service: 'Medical', number: '103', available: '24/7' },
      { service: 'Fire', number: '101', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Moscow City Clinical Hospital', type: 'hospital', address: 'Moscow', phone: '+7 495-681-1111', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'Check current US Embassy status', number: '+7 495-728-5000', available: 'Verify availability' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Мне нужен врач', pronunciation: 'mnye noo-zhen vrach' },
      { english: 'Hospital', local: 'Больница', pronunciation: 'bol-nee-tsa' },
      { english: 'Help me', local: 'Помогите мне', pronunciation: 'poh-moh-gee-tyeh mnye' },
    ],
  },

  // Bhutan
  bhutan: {
    emergencyNumbers: [
      { service: 'Police', number: '113', available: '24/7' },
      { service: 'Medical', number: '112', available: '24/7' },
      { service: 'Fire', number: '110', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Jigme Dorji Wangchuck National Referral Hospital', type: 'hospital', address: 'Thimphu', phone: '+975 2-322496', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy New Delhi (covers Bhutan)', number: '+91 11-2419-8000', available: 'Mon-Fri' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'ང་ལ་སྨན་པ་དགོས་', pronunciation: 'nga-la men-pa gos' },
      { english: 'Hospital', local: 'སྨན་ཁང་', pronunciation: 'men-khang' },
      { english: 'Help me', local: 'རོགས་གནང་།', pronunciation: 'rog-nang' },
    ],
  },

  // Afghanistan
  afghanistan: {
    emergencyNumbers: [
      { service: 'Police', number: '119', available: 'Limited' },
      { service: 'Medical', number: '102', available: 'Limited' },
    ],
    medicalFacilities: [
      { name: 'Emergency Hospital', type: 'hospital', address: 'Kabul', phone: 'Contact through aid organizations', hours: 'Varies' },
    ],
    travelHotlines: [
      { service: 'Check current embassy status', number: 'Contact State Department', available: 'N/A' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'من به دکتر نیاز دارم', pronunciation: 'man be doktor nee-az dah-ram' },
      { english: 'Hospital', local: 'شفاخانه', pronunciation: 'sheh-fa-kha-neh' },
      { english: 'Help me', local: 'کمکم کنید', pronunciation: 'ko-mak-am ko-nid' },
    ],
  },

  // Yemen
  yemen: {
    emergencyNumbers: [
      { service: 'Police', number: '194', available: 'Limited' },
      { service: 'Medical', number: '191', available: 'Limited' },
    ],
    medicalFacilities: [
      { name: 'Al-Thawra Hospital', type: 'hospital', address: 'Sana\'a', phone: 'Limited availability', hours: 'Varies' },
    ],
    travelHotlines: [
      { service: 'Check current embassy status', number: 'Contact State Department', available: 'N/A' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'أحتاج طبيب', pronunciation: 'ah-taj ta-beeb' },
      { english: 'Hospital', local: 'مستشفى', pronunciation: 'mus-tash-fa' },
      { english: 'Help me', local: 'ساعدني', pronunciation: 'sah-id-nee' },
    ],
  },

  // Somalia
  somalia: {
    emergencyNumbers: [
      { service: 'Emergency', number: '888', available: 'Limited' },
    ],
    medicalFacilities: [
      { name: 'Mogadishu Somalia Turkey Recep Tayyip Erdogan Hospital', type: 'hospital', address: 'Mogadishu', phone: 'Limited availability', hours: 'Varies' },
    ],
    travelHotlines: [
      { service: 'Contact State Department', number: 'N/A', available: 'N/A' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Waxaan u baahanahay dhakhtar', pronunciation: 'wah-haan oo bah-ha-nah-hay dhak-tar' },
      { english: 'Hospital', local: 'Isbitaal' },
      { english: 'Help me', local: 'I caawi', pronunciation: 'ee kah-wee' },
    ],
  },

  // Eritrea
  eritrea: {
    emergencyNumbers: [
      { service: 'Police', number: '113', available: '24/7' },
      { service: 'Medical', number: '114', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Orotta Hospital', type: 'hospital', address: 'Asmara', phone: '+291 1-118844', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Asmara', number: '+291 1-120004', available: 'Check availability' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'ሓኪም የድልየኒ', pronunciation: 'ha-keem yeh-deel-yeh-nee' },
      { english: 'Hospital', local: 'ሆስፒታል', pronunciation: 'hos-pee-tal' },
      { english: 'Help me', local: 'ሕገዘኒ', pronunciation: 'hee-geh-zeh-nee' },
    ],
  },

  // South Sudan
  'south sudan': {
    emergencyNumbers: [
      { service: 'Emergency', number: '999', available: 'Limited' },
    ],
    medicalFacilities: [
      { name: 'Juba Teaching Hospital', type: 'hospital', address: 'Juba', phone: 'Limited availability', hours: 'Varies' },
    ],
    travelHotlines: [
      { service: 'US Embassy Juba', number: '+211 912-105-188', available: 'Check availability' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English/Arabic spoken' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Help me', local: 'Help me' },
    ],
  },

  // Burundi
  burundi: {
    emergencyNumbers: [
      { service: 'Police', number: '117', available: '24/7' },
      { service: 'Medical', number: '112', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Prince Regent Charles Hospital', type: 'hospital', address: 'Bujumbura', phone: '+257 22-250272', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Bujumbura', number: '+257 22-207-000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Nkeneye umuganga', pronunciation: 'n-keh-neh-yeh oo-moo-gan-ga' },
      { english: 'Hospital', local: 'Ibitaro' },
      { english: 'Help me', local: 'Mfashe', pronunciation: 'm-fah-sheh' },
    ],
  },

  // The Gambia
  'the gambia': {
    emergencyNumbers: [
      { service: 'Police', number: '117', available: '24/7' },
      { service: 'Medical', number: '116', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Edward Francis Small Teaching Hospital', type: 'hospital', address: 'Banjul', phone: '+220 422-2264', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Banjul', number: '+220 439-2856', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English speaking' },
      { english: 'Hospital', local: 'Hospital' },
      { english: 'Help me', local: 'Help me' },
    ],
  },

  // Togo
  togo: {
    emergencyNumbers: [
      { service: 'Police', number: '117', available: '24/7' },
      { service: 'Medical', number: '118', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'CHU Sylvanus Olympio', type: 'hospital', address: 'Lomé', phone: '+228 22-212-501', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Lomé', number: '+228 22-617-717', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'J\'ai besoin d\'un médecin', pronunciation: 'zhay beh-zwahn duhn meh-deh-sahn' },
      { english: 'Hospital', local: 'Hôpital', pronunciation: 'oh-pee-tal' },
      { english: 'Help me', local: 'Aidez-moi', pronunciation: 'eh-day mwah' },
    ],
  },

  // Benin
  benin: {
    emergencyNumbers: [
      { service: 'Police', number: '117', available: '24/7' },
      { service: 'Medical', number: '118', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'CNHU-HKM', type: 'hospital', address: 'Cotonou', phone: '+229 21-300-130', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Cotonou', number: '+229 21-30-06-50', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'J\'ai besoin d\'un médecin', pronunciation: 'zhay beh-zwahn duhn meh-deh-sahn' },
      { english: 'Hospital', local: 'Hôpital', pronunciation: 'oh-pee-tal' },
      { english: 'Help me', local: 'Aidez-moi', pronunciation: 'eh-day mwah' },
    ],
  },

  // Mauritania
  mauritania: {
    emergencyNumbers: [
      { service: 'Police', number: '117', available: '24/7' },
      { service: 'Medical', number: '101', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Centre Hospitalier National', type: 'hospital', address: 'Nouakchott', phone: '+222 45-251-824', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Nouakchott', number: '+222 45-25-26-60', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'أحتاج طبيب', pronunciation: 'ah-taj ta-beeb' },
      { english: 'Hospital', local: 'مستشفى', pronunciation: 'mus-tash-fa' },
      { english: 'Help me', local: 'ساعدني', pronunciation: 'sah-id-nee' },
    ],
  },

  // Western Sahara
  'western sahara': {
    emergencyNumbers: [
      { service: 'Police', number: '19', available: 'Limited' },
      { service: 'Medical', number: '15', available: 'Limited' },
    ],
    medicalFacilities: [
      { name: 'Local medical facilities limited', type: 'clinic', address: 'Varies', phone: 'Contact Moroccan authorities', hours: 'Varies' },
    ],
    travelHotlines: [
      { service: 'Contact Moroccan authorities', number: 'N/A', available: 'N/A' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'أحتاج طبيب', pronunciation: 'ah-taj ta-beeb' },
      { english: 'Hospital', local: 'مستشفى', pronunciation: 'mus-tash-fa' },
      { english: 'Help me', local: 'ساعدني', pronunciation: 'sah-id-nee' },
    ],
  },

  // Angola
  angola: {
    emergencyNumbers: [
      { service: 'Police', number: '113', available: '24/7' },
      { service: 'Medical', number: '112', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Hospital Américo Boavida', type: 'hospital', address: 'Luanda', phone: '+244 222-320-262', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Luanda', number: '+244 222-641-000', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'Preciso de um médico', pronunciation: 'preh-see-zo deh oom meh-dee-ko' },
      { english: 'Hospital', local: 'Hospital', pronunciation: 'os-pee-tal' },
      { english: 'Help me', local: 'Ajude-me', pronunciation: 'ah-zhoo-deh-meh' },
    ],
  },

  // North Korea
  'north korea': {
    emergencyNumbers: [
      { service: 'Contact tour guide', number: 'Through hotel', available: 'Via guide only' },
    ],
    medicalFacilities: [
      { name: 'Pyongyang Friendship Hospital', type: 'hospital', address: 'Pyongyang', phone: 'Through tour operator', hours: 'Via guide' },
    ],
    travelHotlines: [
      { service: 'Contact tour operator', number: 'N/A', available: 'N/A' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: '의사가 필요합니다', pronunciation: 'ui-sa-ga pil-yo-ham-ni-da' },
      { english: 'Hospital', local: '병원', pronunciation: 'byeong-won' },
      { english: 'Help me', local: '도와주세요', pronunciation: 'do-wa-ju-se-yo' },
    ],
  },

  // Papua New Guinea  
  'papua new guinea': {
    emergencyNumbers: [
      { service: 'Police', number: '000', available: '24/7' },
      { service: 'Medical', number: '111', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Port Moresby General Hospital', type: 'hospital', address: 'Port Moresby', phone: '+675 324-8200', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Port Moresby', number: '+675 321-1455', available: 'Mon-Fri 8:00-17:00' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English/Tok Pisin spoken' },
      { english: 'Hospital', local: 'Hospital/Haus sik' },
      { english: 'Help me', local: 'Help me/Helpim mi' },
    ],
  },

  // Seychelles
  seychelles: {
    emergencyNumbers: [
      { service: 'Police', number: '999', available: '24/7' },
      { service: 'Medical', number: '999', available: '24/7' },
    ],
    medicalFacilities: [
      { name: 'Seychelles Hospital', type: 'hospital', address: 'Victoria, Mahé', phone: '+248 438-8000', hours: '24/7' },
    ],
    travelHotlines: [
      { service: 'US Embassy Mauritius (covers Seychelles)', number: '+230 202-4400', available: 'Mon-Fri' },
    ],
    keyPhrases: [
      { english: 'I need a doctor', local: 'I need a doctor', pronunciation: 'English/French/Creole spoken' },
      { english: 'Hospital', local: 'Hospital/Lopital' },
      { english: 'Help me', local: 'Help me/Ede mwan' },
    ],
  },
};

// City to country mapping for better location detection
const cityToCountryMap: { [key: string]: string } = {
  // Thailand
  'bangkok': 'thailand',
  'phuket': 'thailand',
  'chiang mai': 'thailand',
  'pattaya': 'thailand',
  'krabi': 'thailand',
  'koh samui': 'thailand',
  
  // Japan
  'tokyo': 'japan',
  'osaka': 'japan',
  'kyoto': 'japan',
  'hiroshima': 'japan',
  'fukuoka': 'japan',
  'sapporo': 'japan',
  'nagoya': 'japan',
  'nara': 'japan',
  'yokohama': 'japan',
  
  // France
  'paris': 'france',
  'marseille': 'france',
  'lyon': 'france',
  'nice': 'france',
  'cannes': 'france',
  'bordeaux': 'france',
  'toulouse': 'france',
  'strasbourg': 'france',
  
  // Spain
  'madrid': 'spain',
  'barcelona': 'spain',
  'seville': 'spain',
  'valencia': 'spain',
  'malaga': 'spain',
  'granada': 'spain',
  'bilbao': 'spain',
  'ibiza': 'spain',
  
  // United Kingdom
  'london': 'united kingdom',
  'manchester': 'united kingdom',
  'edinburgh': 'united kingdom',
  'glasgow': 'united kingdom',
  'birmingham': 'united kingdom',
  'liverpool': 'united kingdom',
  'bristol': 'united kingdom',
  'cambridge': 'united kingdom',
  'oxford': 'united kingdom',
  
  // Mexico
  'mexico city': 'mexico',
  'cancun': 'mexico',
  'playa del carmen': 'mexico',
  'cabo': 'mexico',
  'guadalajara': 'mexico',
  'monterrey': 'mexico',
  'tulum': 'mexico',
  'puerto vallarta': 'mexico',
  
  // Italy
  'rome': 'italy',
  'milan': 'italy',
  'florence': 'italy',
  'venice': 'italy',
  'naples': 'italy',
  'bologna': 'italy',
  'verona': 'italy',
  'pisa': 'italy',
  'amalfi': 'italy',
  
  // United States
  'new york': 'united states',
  'los angeles': 'united states',
  'chicago': 'united states',
  'san francisco': 'united states',
  'miami': 'united states',
  'boston': 'united states',
  'seattle': 'united states',
  'las vegas': 'united states',
  'orlando': 'united states',
  'washington': 'united states',
  
  // Canada
  'toronto': 'canada',
  'vancouver': 'canada',
  'montreal': 'canada',
  'ottawa': 'canada',
  'calgary': 'canada',
  'quebec city': 'canada',
  'winnipeg': 'canada',
  'edmonton': 'canada',
  
  // Cyprus
  'nicosia': 'cyprus',
  'limassol': 'cyprus',
  'larnaca': 'cyprus',
  'paphos': 'cyprus',
  
  // Malta
  'valletta': 'malta',
  'sliema': 'malta',
  'st julian\'s': 'malta',
  
  // Andorra
  'andorra la vella': 'andorra',
  
  // Luxembourg
  'luxembourg city': 'luxembourg',
  
  // Gibraltar
  'gibraltar': 'gibraltar',
  
  // Kosovo
  'pristina': 'kosovo',
  'prizren': 'kosovo',
  
  // North Macedonia
  'skopje': 'north macedonia',
  'ohrid': 'north macedonia',
  
  // Ukraine
  'kyiv': 'ukraine',
  'kiev': 'ukraine',
  'lviv': 'ukraine',
  'odessa': 'ukraine',
  
  // Russia
  'moscow': 'russia',
  'st petersburg': 'russia',
  'vladivostok': 'russia',
  
  // Bhutan
  'thimphu': 'bhutan',
  'paro': 'bhutan',
  
  // Afghanistan
  'kabul': 'afghanistan',
  
  // Yemen
  'sana\'a': 'yemen',
  'aden': 'yemen',
  
  // Somalia
  'mogadishu': 'somalia',
  
  // Eritrea
  'asmara': 'eritrea',
  
  // South Sudan
  'juba': 'south sudan',
  
  // Burundi
  'bujumbura': 'burundi',
  
  // The Gambia
  'banjul': 'the gambia',
  
  // Togo
  'lomé': 'togo',
  
  // Benin
  'cotonou': 'benin',
  'porto-novo': 'benin',
  
  // Mauritania
  'nouakchott': 'mauritania',
  
  // Angola
  'luanda': 'angola',
  
  // North Korea
  'pyongyang': 'north korea',
  
  // Papua New Guinea
  'port moresby': 'papua new guinea',
  
  // Seychelles
  'victoria': 'seychelles',
  'mahé': 'seychelles',
  
  // Australia
  'sydney': 'australia',
  'melbourne': 'australia',
  'brisbane': 'australia',
  'perth': 'australia',
  'adelaide': 'australia',
  'gold coast': 'australia',
  
  // Germany
  'berlin': 'germany',
  'munich': 'germany',
  'frankfurt': 'germany',
  'hamburg': 'germany',
  'cologne': 'germany',
  'dresden': 'germany',
  
  // Greece
  'athens': 'greece',
  'santorini': 'greece',
  'mykonos': 'greece',
  'crete': 'greece',
  'rhodes': 'greece',
  'thessaloniki': 'greece',
  
  // Portugal
  'lisbon': 'portugal',
  'porto': 'portugal',
  'algarve': 'portugal',
  'faro': 'portugal',
  'madeira': 'portugal',
  
  // UAE
  'dubai': 'uae',
  'abu dhabi': 'uae',
  'sharjah': 'uae',
  
  // Singapore
  'singapore': 'singapore',
  
  // South Korea
  'seoul': 'south korea',
  'busan': 'south korea',
  'jeju': 'south korea',
  'incheon': 'south korea',
  
  // Vietnam
  'ho chi minh': 'vietnam',
  'saigon': 'vietnam',
  'hanoi': 'vietnam',
  'da nang': 'vietnam',
  'hoi an': 'vietnam',
  'nha trang': 'vietnam',
  
  // Indonesia
  'bali': 'indonesia',
  'jakarta': 'indonesia',
  'denpasar': 'indonesia',
  'ubud': 'indonesia',
  'yogyakarta': 'indonesia',
  
  // New Zealand
  'auckland': 'new zealand',
  'wellington': 'new zealand',
  'christchurch': 'new zealand',
  'queenstown': 'new zealand',
  
  // Netherlands
  'amsterdam': 'netherlands',
  'rotterdam': 'netherlands',
  'the hague': 'netherlands',
  'utrecht': 'netherlands',
  
  // Switzerland
  'zurich': 'switzerland',
  'geneva': 'switzerland',
  'bern': 'switzerland',
  'lucerne': 'switzerland',
  'interlaken': 'switzerland',
  
  // Turkey
  'istanbul': 'turkey',
  'ankara': 'turkey',
  'antalya': 'turkey',
  'cappadocia': 'turkey',
  'bodrum': 'turkey',
  
  // Brazil
  'rio de janeiro': 'brazil',
  'sao paulo': 'brazil',
  'brasilia': 'brazil',
  'salvador': 'brazil',
  
  // Iceland
  'reykjavik': 'iceland',
  
  // India
  'delhi': 'india',
  'mumbai': 'india',
  'bangalore': 'india',
  'jaipur': 'india',
  'goa': 'india',
  'agra': 'india',
  'kolkata': 'india',
  
  // China
  'beijing': 'china',
  'shanghai': 'china',
  'guangzhou': 'china',
  'shenzhen': 'china',
  'hong kong': 'china',
  'xian': 'china',
  
  // Malaysia
  'kuala lumpur': 'malaysia',
  'penang': 'malaysia',
  'langkawi': 'malaysia',
  'malacca': 'malaysia',
};

export function getEmergencyResources(destination: string): EmergencyResources {
  const normalizedDestination = destination.toLowerCase().trim();
  
  // First, try to match by city name
  for (const [city, country] of Object.entries(cityToCountryMap)) {
    if (normalizedDestination.includes(city)) {
      return emergencyResourcesDatabase[country] || emergencyResourcesDatabase.default;
    }
  }
  
  // Then try to match by country name in the destination string
  for (const [country, resources] of Object.entries(emergencyResourcesDatabase)) {
    if (country !== 'default' && normalizedDestination.includes(country)) {
      return resources;
    }
  }
  
  // Return default resources if no match found
  return emergencyResourcesDatabase.default;
}
